import React, { useState } from 'react';
import classNames from 'classnames';

import Button from 'src/components/ui/button';
import IconButton from 'src/components/ui/icon-button';
import { ReactComponent as CheckIcon } from 'src/assets/icons/check.svg';
import { ReactComponent as MinusIcon } from 'src/assets/icons/minus.svg';
import { ReactComponent as PlusIcon } from 'src/assets/icons/plus.svg';

import { LOCALES } from 'src/constants';
import { formatMoney } from 'src/utils';
import type { ItemCounterProps } from './item-counter.props';

import './item-counter.scss';

const ItemCounter = ({ count, maxCount, price, decrement, increment }: ItemCounterProps) => {
  const [isLoaded, setIsLoaded] = useState(true);
  const counterType = maxCount > 1 ? 'multi' : 'single';
  const formattedTotalSum = formatMoney(price * count);

  const buttonContainerClasses = classNames('item-counter__button-container', {
    'item-counter__button-container_added': count && counterType === 'single',
    'item-counter__button-container_plus': count && counterType === 'multi',
  });

  const buttonClasses = classNames('item-counter__button', 'item-counter__button_add', {
    'item-counter__button_added': count && counterType === 'single',
    'item-counter__button_plus': count && counterType === 'multi',
    'item-counter__button_fadein': !count,
    'item-counter__button_without-animation': isLoaded,
  });

  const handleButtonPlusClick = () => {
    increment();
    setIsLoaded(false);
  };

  const renderItemCounter = () => {
    switch (counterType) {
      case 'single':
        return (
          <>
            {count ? (
              <IconButton
                className="item-counter__button"
                onClick={decrement}
                ariaLabel={LOCALES.REMOVE_ITEM}
                icon={<MinusIcon />}
                isTabletSizeGrowth
                theme="outlined"
                size="normal"
              />
            ) : null}
            <div className={buttonContainerClasses}>
              <Button
                className={buttonClasses}
                icon={count ? <CheckIcon /> : null}
                isDisabled={!!count}
                isTabletSizeGrowth
                label={count ? LOCALES.ADDED : LOCALES.ADD_TO_ORDER}
                onClick={increment}
                size="normal"
                theme="ghost"
              />
            </div>
          </>
        );
      case 'multi':
        return (
          <>
            {count ? (
              <>
                <IconButton
                  className="item-counter__button"
                  onClick={decrement}
                  ariaLabel={LOCALES.REMOVE_ITEM}
                  icon={<MinusIcon />}
                  isTabletSizeGrowth
                  theme="outlined"
                  size="normal"
                />
                <span className="item-counter__count">{count}</span>
              </>
            ) : null}
            <div className={buttonContainerClasses}>
              <Button
                className={buttonClasses}
                icon={count ? <PlusIcon /> : null}
                isDisabled={count === maxCount}
                isTabletSizeGrowth
                label={count ? LOCALES.ADD_ITEM : LOCALES.ADD_TO_ORDER}
                onClick={handleButtonPlusClick}
                size="normal"
                theme={count ? 'outlined' : 'ghost'}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const itemCounterClasses = classNames('item-counter', count && `item-counter_${counterType}`, {
    'item-counter_fadein': count,
  });

  return (
    <div className={itemCounterClasses}>
      {renderItemCounter()}
      {count ? (
        <span className="item-counter__total">
          {formattedTotalSum} {LOCALES.TOTAL}
        </span>
      ) : null}
    </div>
  );
};

export default ItemCounter;
